import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Hint } from './Hint';
import { Label } from './Label';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy } from './cx';

export interface FileUploadProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  id: string;
  label: ReactNode;
  labelClassName?: string;
  hint?: ReactNode;
  error?: string;
  /** a red asterisk after the label ("(povinné)" for screen readers) */
  required?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/** govuk/components/file-upload */
export function FileUpload({ id, label, labelClassName, hint, error, required, className, ref, ...rest }: FileUploadProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error')}>
      <Label htmlFor={id} className={labelClassName}>
        {label}
        <RequiredMarker required={required} />
      </Label>
      {hint && <Hint id={hintId}>{hint}</Hint>}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      <input
        ref={ref}
        className={cx('govuk-file-upload', className, error && 'govuk-file-upload--error')}
        id={id}
        type="file"
        aria-describedby={describedBy(hintId, errorId)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </div>
  );
}
